import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTransientAutoCreatedEventContext } from './DraftEventStoreProvider';
import { Organizer } from './screens/Form';
import { PublishError } from './screens/Preview/publishErrors';
import { CoreUserWithActiveOrganization } from './shared';

export type ErrorSimulation = {
    createEventErrorCount: number;
    createEventErrorThreshold: number;
    shouldErrorOnCreateEvent: boolean;

    createTransientEventErrorCount: number;
    createTransientErrorThreshold: number;
    shouldErrorOnCreateTransientEvent: boolean;

    createOrganizationErrorCount: number;
    createOrganizationErrorThreshold: number;
    shouldErrorOnCreateOrganization: boolean;

    createAccountErrorCount: number;
    createAccountErrorThreshold: number;
    shouldErrorOnCreateAccount: boolean;

    publishEventErrorCount: number;
    publishEventErrorThreshold: number;
    shouldErrorOnPublishEvent: boolean;

    saveOnboardingResponsesErrorCount: number;
    saveOnboardingResponsesErrorThreshold: number;
    shouldErrorOnSaveOnboardingResponses: boolean;
};

export interface AppContextState {
    _activeOrganizationId: string;
    _organizerId: string | null;
    _publicId: string | null;
    createdEventId: string | null;
    didCreateAccount: boolean;
    didCreateEvent: boolean;
    dismissedRetrySuccessfulAlert: boolean;
    eventCreationFailed: boolean;
    didCreateAccountRequestFail: boolean;
    didGetOrganizerIdRequestFail: boolean;
    didPublishEventRequestFail: boolean;
    didCreateOrganizationRequestFail: boolean;
    didSaveOnboardingResponsesRequestFail: boolean;
    hasOrganization: boolean;
    didCreateEventRequestFail: boolean;
    didRetry: boolean;
    isLoadingInInnerComponent: boolean;
    isFullPageLoading: boolean;
    isModalShowing: boolean;
    isSubmittingCreateAccountForm: boolean;
    isPerformingAsyncAction: boolean;
    isPublishingEvent: boolean;
    retryCount: number;
    showRetrySuccessfulAlert: boolean;
    wasRetrySuccessful: boolean;
    shouldSendSalesEligibleEvent: boolean;
    isSocialSignUpLoading: boolean;
    shouldShowFullSignUpForm: boolean;
    isSubmitFormButtonDisabled: boolean;
    enteredEmailExists: boolean;
    publishError: PublishError | null;
    phoneNumber: string | null;
    user: CoreUserWithActiveOrganization | null;
    errorSimulation: ErrorSimulation;
    testingPanel: {
        isShowing: boolean;
    };
    updateContext: (newState: Partial<AppContextState>) => void;
}

interface AppContextProviderProps {
    initialValue?: Partial<AppContextState>;
    children: React.ReactNode;
}

const initState = {} as AppContextState;
const AppContext = React.createContext<AppContextState>(initState);

const AppContextProvider: React.FC<AppContextProviderProps> = ({
    children,
    initialValue,
}) => {
    const { user } = initialValue || {};
    const { transientAutoCreatedEvent } = useTransientAutoCreatedEventContext();

    const queryClient = useQueryClient();

    const initialState: AppContextState = {
        _activeOrganizationId: user?.activeOrganization?.id || '',
        _organizerId: null,
        _publicId: user?.publicId || null,
        user: user || null,
        createdEventId: user?.publicId
            ? transientAutoCreatedEvent?.id || null
            : null,
        eventCreationFailed: false,
        dismissedRetrySuccessfulAlert: false,
        isFullPageLoading: false,
        isModalShowing: false,
        isSubmittingCreateAccountForm: false,
        didCreateAccount: !!user?.publicId,
        didCreateEvent: !!user?.publicId && !!transientAutoCreatedEvent?.id,
        didCreateAccountRequestFail: false,
        didCreateOrganizationRequestFail: false,
        didCreateEventRequestFail: false,
        didPublishEventRequestFail: false,
        didGetOrganizerIdRequestFail: false,
        didSaveOnboardingResponsesRequestFail: false,
        didRetry: false,
        hasOrganization:
            user?.activeOrganization != null && user?.publicId != null,
        isLoadingInInnerComponent: false,
        isPerformingAsyncAction: false,
        isPublishingEvent: false,
        retryCount: 0,
        showRetrySuccessfulAlert: false,
        shouldSendSalesEligibleEvent: false,
        phoneNumber: null,
        publishError: null,
        wasRetrySuccessful: false,
        isSocialSignUpLoading: false,
        shouldShowFullSignUpForm: false,
        isSubmitFormButtonDisabled: false,
        enteredEmailExists: false,
        errorSimulation: {
            shouldErrorOnCreateTransientEvent: false,
            shouldErrorOnCreateOrganization: false,
            shouldErrorOnCreateEvent: false,
            shouldErrorOnCreateAccount: false,
            shouldErrorOnPublishEvent: false,
            shouldErrorOnSaveOnboardingResponses: false,
            createEventErrorCount: 0,
            createEventErrorThreshold: 0,
            createTransientEventErrorCount: 0,
            createTransientErrorThreshold: 0,
            createOrganizationErrorCount: 0,
            createOrganizationErrorThreshold: 0,
            createAccountErrorCount: 0,
            createAccountErrorThreshold: 0,
            publishEventErrorCount: 0,
            publishEventErrorThreshold: 0,
            saveOnboardingResponsesErrorCount: 0,
            saveOnboardingResponsesErrorThreshold: 0,
        },
        testingPanel: {
            isShowing: false,
        },
        updateContext: () => {
            console.log('not implemented');
        },
    };

    const organizer = queryClient.getQueryData<Organizer>([
        'organizer-id',
        user?.activeOrganization?.id,
    ]);

    const [state, setState] = useState<AppContextState>({
        ...initialState,
        ...initialValue,
    });

    const updateContext = useCallback((newState: Partial<AppContextState>) => {
        setState((prevState) => {
            const updatedState = {
                ...prevState,
                ...newState,
                errorSimulation: {
                    ...prevState.errorSimulation,
                    ...(newState.errorSimulation && newState.errorSimulation),
                },
            };

            // evaluate eventCreationFailed based on updated state
            updatedState.eventCreationFailed =
                updatedState.didCreateEventRequestFail ||
                updatedState.didCreateOrganizationRequestFail ||
                updatedState.didGetOrganizerIdRequestFail ||
                updatedState.didSaveOnboardingResponsesRequestFail;

            updatedState.wasRetrySuccessful =
                updatedState.didRetry && updatedState.didCreateEvent;

            updatedState.showRetrySuccessfulAlert =
                updatedState.wasRetrySuccessful &&
                updatedState.retryCount > 1 &&
                !updatedState.isModalShowing &&
                !updatedState.dismissedRetrySuccessfulAlert;

            return updatedState;
        });
    }, []);

    useEffect(() => {
        const updates: Partial<AppContextState> = {};
        if (organizer) {
            updates._organizerId = organizer.id;
        }

        if (transientAutoCreatedEvent?.id) {
            updates.createdEventId = transientAutoCreatedEvent.id;
            updates.didCreateEvent = true;
        }

        updateContext(updates);
    }, [organizer, transientAutoCreatedEvent, updateContext]);

    const contextValue = useMemo(
        () => ({ ...state, updateContext }),
        [state, updateContext],
    );

    return (
        <AppContext.Provider value={contextValue}>
            {children}
        </AppContext.Provider>
    );
};

const useAppContext = () => {
    const context = React.useContext(AppContext);
    if (context === undefined) {
        throw new Error(
            'useAppContext must be used within a AppContextProvider',
        );
    }
    return context;
};

export { AppContextProvider, useAppContext };
